import React from 'react';
import { RefreshCcw } from 'lucide-react';

import PriceDistributionView from '../../components/PriceDistributionView';

const ExpandedRunCurrentRows = ({ controller, run, res, ridx, runMinConfidenceThreshold }) => {
  const {
    getFilteredItemEntries,
    getResultTokenCounts,
    getResultAttemptSourceInfo,
    getResultLotNumber,
    getResultImageUrls,
    getDisabledImageKeySet,
    normalizeImageKey,
    getLotActionKey,
    rerunningLotBusy,
    toFiniteNumber,
    computeAccuracyRatio,
    setSelectedImage,
    setSelectedImages,
    setSelectedLotNum,
    setSelectedItems,
    toggleLotImageDisabled,
    rerunSingleLot,
    getReviewNoteDraftKey,
    getTrustedReviewNote,
    reviewNoteDrafts,
    savingReviewNotes,
    setReviewNoteDrafts,
    saveReviewNote,
    lotNoteDrafts,
    setLotNoteDrafts,
    savingLotNotes,
    saveLotReviewNote,
    formatConfidencePercent,
    formatTokenCount,
    getSourceBadgeClassName,
    getSourceDisplayLabel,
    toFiniteCount,
  } = controller;

  const resultItemEntries = getFilteredItemEntries(res?.items, runMinConfidenceThreshold);
  if (resultItemEntries.length === 0) return null;

  const itemCount = resultItemEntries.length;
  const resultTokenTotals = getResultTokenCounts(res);
  const sourceInfo = getResultAttemptSourceInfo(res);
  const firstResultItem = (resultItemEntries.find((entry) => entry.item)?.item) || null;
  const lotNumber = getResultLotNumber(res, firstResultItem);
  const actionLotNumber = String(
    lotNumber ||
      getResultLotNumber(res) ||
      (firstResultItem ? getResultLotNumber({}, firstResultItem) : '') ||
      '',
  ).trim();
  const runId = String(run?.run_id || '').trim();
  const lotActionKey = getLotActionKey(runId, actionLotNumber || ridx);
  const isRerunningLot = !!rerunningLotBusy[lotActionKey];
  const resultImageUrls = getResultImageUrls(res, firstResultItem);
  const disabledImageKeySet = getDisabledImageKeySet(res);
  const activeImageKeySet = new Set(
    resultImageUrls
      .map((url) => normalizeImageKey(url) || String(url || '').trim().toLowerCase())
      .filter((imageKey) => imageKey && !disabledImageKeySet.has(imageKey)),
  );
  const visibleResultItemEntries = resultItemEntries.filter(
    (entry) => entry.item && typeof entry.item === 'object',
  );
  const visibleEstimatedSum = resultItemEntries.reduce(
    (sum, entry) =>
      entry.item && typeof entry.item === 'object'
        ? sum + (toFiniteNumber(entry?.item?.estimated) || 0)
        : sum,
    0,
  );
  const visibleLotActualValue =
    visibleResultItemEntries
      .map((entry) => toFiniteNumber(entry?.item?.actual))
      .find((value) => value !== null) ?? null;
  const activeImageCount = activeImageKeySet.size;
  const canRerunLot = Boolean(
    runId && actionLotNumber && resultImageUrls.length > 0 && activeImageCount > 0,
  );

  return (
    <React.Fragment>
      {resultItemEntries.map(({ item, originalIndex }, iidx) => {
        const rowItem = item && typeof item === 'object' ? item : {};
        const isFilteredOutRow = originalIndex === null;
        const hasConcreteItemIndex = Number.isFinite(Number(originalIndex));
        const rowLotNumber = getResultLotNumber(res, rowItem) || lotNumber;
        const imageUrlsForModal = getResultImageUrls(res, rowItem);
        const estimatedValue = toFiniteNumber(rowItem.estimated);
        const reviewNoteDraftKey = hasConcreteItemIndex
          ? getReviewNoteDraftKey({
              runId,
              lotNumber: rowLotNumber || lotNumber || ridx,
              resultIndex: ridx,
              itemIndex: originalIndex,
            })
          : '';
        const persistedReviewNote = hasConcreteItemIndex ? getTrustedReviewNote(rowItem) : '';
        const reviewNoteValue = hasConcreteItemIndex
          ? Object.prototype.hasOwnProperty.call(reviewNoteDrafts, reviewNoteDraftKey)
            ? reviewNoteDrafts[reviewNoteDraftKey]
            : persistedReviewNote
          : '';
        const isSavingReviewNote = hasConcreteItemIndex
          ? !!savingReviewNotes[reviewNoteDraftKey]
          : false;
        const hasReviewNoteChanges = hasConcreteItemIndex
          ? reviewNoteValue !== persistedReviewNote
          : false;
        const canSaveReviewNote = hasConcreteItemIndex && Boolean(runId && (rowLotNumber || lotNumber));
        const canSaveLotNote = Boolean(runId && (lotNumber || rowLotNumber || ridx));

        return (
          <tr key={`${ridx}-${originalIndex}`} className={`${iidx < itemCount - 1 ? '' : 'border-b'} hover:bg-slate-50`}>
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                <span>{rowLotNumber || '-'}</span>
              </td>
            )}
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                <div className="flex flex-wrap gap-1">
                  {resultImageUrls.map((url, uidx) => {
                    const imageKey = normalizeImageKey(url) || String(url || '').trim().toLowerCase();
                    const isDisabledImage = disabledImageKeySet.has(imageKey);
                    const canToggleImage = Boolean(
                      runId && (actionLotNumber || rowLotNumber || lotNumber) && String(url || '').trim(),
                    );
                    return (
                      <div key={uidx} className="flex flex-col items-center gap-0.5">
                        <div className="relative">
                          <img
                            src={url}
                            alt={`${rowItem.item || rowItem.item_name || 'Lot image'} ${uidx + 1}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImage(url);
                              setSelectedImages(imageUrlsForModal);
                              setSelectedLotNum(rowLotNumber);
                              setSelectedItems(res.items);
                            }}
                            className={`w-10 h-10 object-cover rounded shadow-sm hover:scale-[1.5] hover:z-10 transition-transform cursor-pointer ring-2 ${isDisabledImage ? 'grayscale opacity-35 ring-red-400' : 'ring-emerald-400'}`}
                          />
                          {isDisabledImage && (
                            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white bg-slate-900/55 rounded">
                              OFF
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            if (!canToggleImage) return;
                            toggleLotImageDisabled({
                              runId,
                              lotNumber: actionLotNumber || rowLotNumber || lotNumber,
                              imageUrl: url,
                              disable: !isDisabledImage,
                              resultIndex: ridx,
                            });
                          }}
                          disabled={!canToggleImage || isRerunningLot}
                          className={`rounded border px-1 py-0.5 text-[9px] leading-none font-semibold transition-colors ${!canToggleImage || isRerunningLot ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed' : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'}`}
                          title={isDisabledImage ? 'Turn on image for this lot' : 'Turn off image for this lot'}
                        >
                          {isDisabledImage ? 'On' : 'Off'}
                        </button>
                      </div>
                    );
                  })}
                  {resultImageUrls.length === 0 && (
                    <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center text-[10px] text-slate-400">
                      No Img
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 mt-2">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      rerunSingleLot({
                        runId,
                        lotNumber: actionLotNumber || rowLotNumber || lotNumber,
                      });
                    }}
                    disabled={!canRerunLot || isRerunningLot}
                    className={`inline-flex items-center rounded border px-2 py-1 text-[10px] font-semibold transition-colors ${!canRerunLot || isRerunningLot ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed' : 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                    title={
                      !runId
                        ? 'This run has no run_id, rerun is unavailable.'
                        : !(actionLotNumber || lotNumber)
                          ? 'Missing lot number.'
                          : activeImageCount <= 0
                            ? 'Enable at least one image before rerun.'
                            : 'Rerun this lot with enabled images only'
                    }
                  >
                    <RefreshCcw size={10} className={`mr-1 ${isRerunningLot ? 'animate-spin' : ''}`} />
                    {isRerunningLot ? 'Rerunning...' : 'Rerun lot'}
                  </button>
                  {Array.isArray(res.rerun_history) && res.rerun_history.length > 0 && (
                    <span
                      className="inline-flex items-center rounded border border-purple-300 bg-purple-50 text-purple-700 px-2 py-1 text-[10px] font-semibold whitespace-nowrap"
                      title="Previous image-combination rerun attempts shown below"
                    >
                      Hist ({res.rerun_history.length})
                    </span>
                  )}
                </div>
              </td>
            )}

            <td className="px-1.5 py-1 border-r font-medium">
              {isFilteredOutRow ? (
                <span className="text-slate-400">-</span>
              ) : (
                <div className="break-words" title={rowItem.item || rowItem.item_name || '-'}>
                  {rowItem.item || rowItem.item_name || '-'}
                </div>
              )}
            </td>
            <td className="px-1.5 py-1 border-r">
              {isFilteredOutRow ? <span className="text-slate-300">-</span> : estimatedValue === null ? '-' : `$${estimatedValue.toFixed(2)}`}
            </td>
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top font-bold" rowSpan={itemCount}>
                ${visibleEstimatedSum.toFixed(2)}
              </td>
            )}
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                {visibleLotActualValue === null ? '-' : `$${visibleLotActualValue.toFixed(2)}`}
              </td>
            )}

            <td className="px-1.5 py-1 border-r text-slate-600">
              {isFilteredOutRow ? (
                <div className="text-[10px] text-slate-400 italic">Filtered out by confidence</div>
              ) : (
                (() => {
                  const condition = rowItem?.condition || 'Unknown';
                  const priceSource = rowItem?.price_source || 'model_knowledge';
                  const confidenceLabel = formatConfidencePercent(rowItem?.confidence_score, '-');
                  const sourceLabel = priceSource === 'ebay_rag' ? 'eBay RAG' : priceSource === 'image' ? 'Image' : 'Model Knowledge';
                  const sourceBg = priceSource === 'ebay_rag' ? 'bg-blue-100 text-blue-700' : priceSource === 'image' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
                  const condBg = condition === 'New' ? 'bg-green-100 text-green-700' : condition === 'Damaged' || condition === 'For Parts' ? 'bg-red-100 text-red-700' : condition === 'Unknown' ? 'bg-slate-100 text-slate-500' : 'bg-yellow-100 text-yellow-700';
                  return (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1"><span className="text-[8px] text-slate-400">Condition:</span><span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${condBg}`}>{condition}</span></div>
                      <div className="flex items-center gap-1"><span className="text-[8px] text-slate-400">Price Src:</span><span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${sourceBg}`}>{sourceLabel}</span></div>
                      <div className="flex items-center gap-1"><span className="text-[8px] text-slate-400">Conf:</span><span className="inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold bg-indigo-100 text-indigo-700">{confidenceLabel}</span></div>
                      <PriceDistributionView rangeValue={rowItem?.estimated_value_range} distributionValue={rowItem?.price_estimate_distribution} maxBins={Number.MAX_SAFE_INTEGER} compact className="mt-0.5" />
                    </div>
                  );
                })()
              )}
            </td>

            <td className="px-1.5 py-1 border-r align-top">
              {isFilteredOutRow ? (
                <span className="text-[10px] text-slate-400">-</span>
              ) : (
                <div className="space-y-1">
                  <textarea value={reviewNoteValue} onChange={(event) => { const nextValue = event.target.value.slice(0, 4000); setReviewNoteDrafts((previous) => ({ ...previous, [reviewNoteDraftKey]: nextValue })); }} maxLength={4000} placeholder="Add subitem note..." className="w-full min-h-[48px] rounded border border-slate-300 px-1.5 py-1 text-[10px] outline-none focus:border-blue-500" />
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-400">{reviewNoteValue.length}/4000</span>
                    <button type="button" onClick={(event) => { event.stopPropagation(); if (!canSaveReviewNote || isSavingReviewNote || !hasReviewNoteChanges) return; saveReviewNote({ runId, lotNumber: rowLotNumber || lotNumber, resultIndex: ridx, itemIndex: originalIndex, reviewNote: reviewNoteValue }); }} disabled={!canSaveReviewNote || isSavingReviewNote || !hasReviewNoteChanges} className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${!canSaveReviewNote || isSavingReviewNote || !hasReviewNoteChanges ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200' : 'bg-blue-600 text-white hover:bg-blue-700 border border-blue-600'}`}>{isSavingReviewNote ? 'Saving...' : 'Save Note'}</button>
                  </div>
                </div>
              )}
            </td>

            {iidx === 0 && (() => {
              const lotDraftKey = `lot-${runId}-${lotNumber || ridx}-${ridx}`;
              const persistedLotNote = typeof res.lot_review_note === 'string' ? res.lot_review_note : '';
              const lotNoteValue = Object.prototype.hasOwnProperty.call(lotNoteDrafts, lotDraftKey) ? lotNoteDrafts[lotDraftKey] : persistedLotNote;
              const isSavingLotNote = !!savingLotNotes[lotDraftKey];
              const hasLotNoteChanges = lotNoteValue !== persistedLotNote;
              return (
                <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                  <div className="space-y-1">
                    <textarea value={lotNoteValue} onChange={(e) => { const v = e.target.value.slice(0, 4000); setLotNoteDrafts((prev) => ({ ...prev, [lotDraftKey]: v })); }} maxLength={4000} placeholder="Add item note..." className="w-full min-h-[48px] rounded border border-slate-300 px-1.5 py-1 text-[10px] outline-none focus:border-green-500" />
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-400">{lotNoteValue.length}/4000</span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); if (!canSaveLotNote || isSavingLotNote || !hasLotNoteChanges) return; saveLotReviewNote({ runId, lotNumber: lotNumber || ridx, resultIndex: ridx, reviewNote: lotNoteValue }); }} disabled={!canSaveLotNote || isSavingLotNote || !hasLotNoteChanges} className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${!canSaveLotNote || isSavingLotNote || !hasLotNoteChanges ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200' : 'bg-green-600 text-white hover:bg-green-700 border border-green-600'}`}>{isSavingLotNote ? 'Saving...' : 'Save'}</button>
                    </div>
                  </div>
                </td>
              );
            })()}

            {iidx === 0 && (() => {
              const aiSum = visibleEstimatedSum;
              const actual = visibleLotActualValue;
              const acc = computeAccuracyRatio(actual, aiSum);
              return (
                <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                  {acc === null ? '-' : <span className={`px-1 py-0.5 rounded font-bold text-[10px] ${acc > 0.8 ? 'bg-green-100 text-green-700' : acc > 0.5 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{(acc * 100).toFixed(1)}%</span>}
                </td>
              );
            })()}
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                {(() => {
                  const durSec = toFiniteNumber(res.duration);
                  const durMs = toFiniteNumber(res.duration_ms);
                  const dur = durMs > 0 ? durMs / 1000 : durSec > 0 ? durSec : 0;
                  const liveDur = toFiniteNumber(res.last_live_duration);
                  const displayDur = dur > 0 ? dur : liveDur;
                  return displayDur != null && displayDur > 0 ? `${displayDur.toFixed(1)}s` : '0ms';
                })()}
              </td>
            )}
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                <span className={`inline-flex rounded px-1 py-0.5 text-[9px] font-semibold whitespace-nowrap ${getSourceBadgeClassName(sourceInfo.finalSource, sourceInfo.finalCacheHit)}`} title={sourceInfo.cacheKey || undefined}>
                  {sourceInfo.finalCacheHit === null ? getSourceDisplayLabel(sourceInfo.finalSource) : sourceInfo.finalCacheHit ? 'CACHE' : 'LIVE'}
                </span>
              </td>
            )}
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold ${toFiniteCount(resultTokenTotals.input) !== null ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>{formatTokenCount(resultTokenTotals.input)}</span>
              </td>
            )}
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold ${toFiniteCount(resultTokenTotals.output) !== null ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`}>{formatTokenCount(resultTokenTotals.output)}</span>
              </td>
            )}
            {iidx === 0 && (
              <td className="px-1.5 py-1 align-top" rowSpan={itemCount}>
                {controller.formatCostDisplay(res.cost)}
              </td>
            )}
          </tr>
        );
      })}
    </React.Fragment>
  );
};

export default ExpandedRunCurrentRows;
